import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";

import {
  deleteData,
  getAlert,
  getData,
  getDataWithComp,
  insertData,
  updateData,
} from "../../../../utils/SamuiUtils";
import ApModal from "../../../components/Modal/ApModal";
import ArModal from "../../../components/Modal/ArModal";
import CompModal from "../../../components/Modal/CompModal";
import BankModal from "../../../components/Modal/BankModal";

const buildDefaultForm = () => ({
  Bank_Master_Id: "",
  Bank_Name_TH: "",
  Bank_Name_EN: "",
  Bank_Status: "Y",
  Bank_Account: "",
  Bank_Type: "SA",
  Comp_Id: window.localStorage.getItem("company") || "",
  APAR_Type: "AP",
  APAR_Id: "",
});

const BankForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
  masterList = [],
}) => {
  const [initialFormData, setInitialFormData] = useState(buildDefaultForm());
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const [apDataList, setApDataList] = useState([]);
  const [arDataList, setArDataList] = useState([]);
  const [compDataList, setCompDataList] = useState([]);
  const [bankDataList, setBankDataList] = useState([]);
  const [selectName, setSelectName] = useState("");
  const [selectBankName, setSelectBankName] = useState("");
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);

  const companyId = useMemo(
    () => window.localStorage.getItem("company") || "",
    []
  );

  const loadLookups = useCallback(async () => {
    try {
      const apList = await getDataWithComp(
        "Tb_Set_AP",
        "ORDER BY AP_Code ASC"
      );
      if (apList?.length) {
        setApDataList(apList);
      }

      const arList = await getDataWithComp(
        "Tb_Set_AR",
        "ORDER BY AR_Code ASC"
      );
      if (arList?.length) {
        setArDataList(arList);
      }

      const compList = await getData(
        "Tb_Set_Company",
        "1 = 1 ORDER BY Comp_Id ASC"
      );
      if (compList?.length) {
        setCompDataList(compList);
      }

      const bankList = await getData(
        "Tb_Set_Bank_Master",
        "1 = 1 ORDER BY Bank_Master_Id ASC"
      );
      if (bankList?.length) {
        setBankDataList(bankList);
      }
    } catch (error) {
      getAlert("FAILED", error.message);
    }
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (mode === "I") {
      const defaults = buildDefaultForm();
      setFormData(defaults);
      setInitialFormData(defaults);
      setSelectName("");
      setSelectBankName("");
    } else if (mode === "U" && selectedData) {
      const editData = {
        Bank_Id: selectedData.Bank_Id,
        Bank_Name_TH: selectedData.Bank_Name_TH || "",
        Bank_Name_EN: selectedData.Bank_Name_EN || "",
        Bank_Status: selectedData.Bank_Status || "Y",
        Bank_Account: selectedData.Bank_Account || "",
        Bank_Type: selectedData.Bank_Type || "SA",
        Comp_Id: selectedData.Comp_Id || companyId,
        APAR_Type: selectedData.APAR_Type || "AP",
        APAR_Id: selectedData.APAR_Id || "",
        Bank_Master_Id: selectedData.Bank_Master_Id || "",
      };

      setFormData(editData);
      setInitialFormData(editData);
      setSelectName(
        selectedData.CCode && selectedData.NName
          ? `${selectedData.CCode} ${selectedData.NName}`
          : selectedData.NName || ""
      );
      setSelectBankName(selectedData.B_Name_TH || "");
    }
  }, [mode, selectedData, setFormData, companyId]);

  const handleBack = useCallback(() => {
    callInitialize();
  }, [callInitialize]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "APAR_Type") {
      setSelectName("");
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        APAR_Id: "",
      }));
      setInitialFormData((prev) => ({
        ...prev,
        [name]: value,
        APAR_Id: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatAccountNo = (value) => {
    if (!value) return "";
    const digits = String(value).replace(/\D/g, "").slice(0, 10);
    const p1 = digits.slice(0, 3);
    const p2 = digits.slice(3, 6);
    const p3 = digits.slice(6, 10);
    return [p1, p2, p3].filter(Boolean).join("-");
  };

  const handleBankAccountChange = (raw) => {
    const formatted = formatAccountNo(raw);
    setFormData((prev) => ({ ...prev, Bank_Account: formatted }));
  };

  const handleBankAccBlur = () => {
    const digits = String(formData.Bank_Account || "").replace(/\D/g, "");
    if (formData.Bank_Account && digits.length !== 10) {
      try {
        getAlert("WARNING", "รูปแบบเลขที่บัญชีต้องเป็น xxx-xxx-xxxx");
      } catch (e) {
        console.warn("Invalid account format");
      }
    }
  };

  const handleSelectBank = (bank) => {
    setSelectBankName(bank.B_Name_TH);
    setFormData((prev) => ({
      ...prev,
      Bank_Master_Id: bank.Bank_Master_Id,
    }));
    setInitialFormData((prev) => ({
      ...prev,
      Bank_Master_Id: bank.Bank_Master_Id,
    }));
    setShowBankModal(false);
  };

  const handleSelectEntity = (dataSelected) => {
    try {
      let aparId = "";
      if (formData.APAR_Type === "AP") {
        aparId = dataSelected.AP_Id;
        setSelectName(`${dataSelected.AP_Code} ${dataSelected.AP_Name}`);
      } else if (formData.APAR_Type === "AR") {
        aparId = dataSelected.AR_Id;
        setSelectName(`${dataSelected.AR_Code} ${dataSelected.AR_Name}`);
      } else if (formData.APAR_Type === "CP") {
        aparId = dataSelected.Comp_Id;
        setSelectName(dataSelected.Comp_Name_TH);
      }

      setFormData((prev) => ({
        ...prev,
        APAR_Id: aparId,
      }));
      setInitialFormData((prev) => ({
        ...prev,
        APAR_Id: aparId,
      }));
      setShowEntityModal(false);
    } catch (error) {
      getAlert("FAILED", error);
    }
  };

  const handleInsert = useCallback(async () => {
    try {
      const { Bank_Id, ...dataToInsert } = formData;
      const fields = Object.keys(dataToInsert).join(", ");
      const values = Object.values(dataToInsert);

      await insertData("Tb_Set_Bank", fields, values);
      return true;
    } catch (error) {
      console.error("Error inserting bank:", error);
      getAlert(
        "FAILED",
        "ไม่สามารถบันทึกข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
      return false;
    }
  }, [formData]);

  const handleUpdate = useCallback(async () => {
    try {
      const where = { Bank_Id: formData.Bank_Id };
      const { Bank_Id, ...values } = formData;
      const res = await updateData("Tb_Set_Bank", values, where);
      if (res) {
        return true;
      }
      getAlert("FAILED", "ไม่สามารถอัปเดตข้อมูลได้");
      return false;
    } catch (error) {
      getAlert(
        "FAILED",
        "ไม่สามารถอัปเดตข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
      return false;
    }
  }, [formData]);

  const handleDelete = async () => {
    if (!selectedData?.Bank_Id) {
      getAlert("WARNING", "ไม่พบข้อมูลที่ต้องการลบ");
      return;
    }

    getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
      if (!isConfirm) return;
      try {
        await deleteData("Tb_Set_Bank", `WHERE Bank_Id = ${selectedData.Bank_Id}`);
        await callInitialize();
      } catch (error) {
        getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
      }
    });
  };

  const handleReset = () => {
    setFormData(initialFormData);
    const resolvedBankName =
      initialFormData.Bank_Master_Id
        ? bankDataList.find(
          (bank) => bank.Bank_Master_Id === initialFormData.Bank_Master_Id
        )?.B_Name_TH || ""
        : "";
    setSelectBankName(resolvedBankName);

    if (initialFormData.APAR_Id) {
      const entityType = initialFormData.APAR_Type;
      const label =
        entityType === "AP"
          ? apDataList.find((item) => item.AP_Id === initialFormData.APAR_Id)
          : entityType === "AR"
            ? arDataList.find((item) => item.AR_Id === initialFormData.APAR_Id)
            : compDataList.find((item) => item.Comp_Id === initialFormData.APAR_Id);

      if (!label) {
        setSelectName("");
        return;
      }

      setSelectName(
        entityType === "CP"
          ? label.Comp_Name_TH
          : `${label.AP_Code || label.AR_Code || ""} ${label.AP_Name || label.AR_Name || ""
          }`
      );
    } else {
      setSelectName("");
    }
  };

  const handleSubmit = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!formData.Bank_Account) {
      getAlert("WARNING", "กรุณากรอกเลขบัญชีธนาคาร");
      return;
    }

    if (!formData.Bank_Master_Id && !formData.APAR_Id) {
      getAlert(
        "WARNING",
        "กรุณากรอกชื่อธนาคาร หรือ เลือกเจ้าหนี้/ลูกหนี้/บริษัท อย่างใดอย่างหนึ่ง"
      );
      return;
    }

    const isDuplicateAccount = masterList.some(
      (item) =>
        item.Bank_Account === formData.Bank_Account &&
        item.Bank_Id !== formData.Bank_Id
    );
    if (isDuplicateAccount) {
      getAlert("WARNING", "เลขบัญชีธนาคารนี้มีอยู่ในระบบแล้ว");
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      const success =
        mode === "I" ? await handleInsert() : await handleUpdate();

      if (!success) {
        return;
      }

      await callInitialize();
      const defaults = buildDefaultForm();
      setFormData(defaults);
      setInitialFormData(defaults);
      setSelectName("");
      setSelectBankName("");
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [formData, masterList, mode, handleInsert, handleUpdate, callInitialize, setFormData]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            handleBack();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit, handleBack]);

  return (
    <>
      <InitialDataFormLayout
        name={name}
        entityPath="/tbsetbank"
        mode={mode}
        onSubmit={handleSubmit}
        isSubmittingDisabled={isSaving}
        onBack={handleBack}
        onReset={handleReset}
        onDelete={mode === "U" ? handleDelete : undefined}
        callInitialize={callInitialize}
      >
        <div style={sectionHeaderStyle}>
          <i className="fas fa-university" style={sectionIconStyle}></i>
          <h5 style={sectionTitleStyle}>ข้อมูลธนาคาร</h5>
        </div>
        <div className="row g-4">
          <div className="col-lg-4 col-md-6">
            <label style={labelStyle}>
              เลขที่บัญชี <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="Bank_Account"
              value={formData.Bank_Account || ""}
              onChange={(e) => handleBankAccountChange(e.target.value)}
              onBlur={handleBankAccBlur}
              placeholder="xxx-xxx-xxxx"
              inputMode="numeric"
              maxLength={13}
              pattern="\d{3}-\d{3}-\d{4}"
              title="รูปแบบเลขที่บัญชี: xxx-xxx-xxxx"
              style={inputStyle}
            />
          </div>
          <div className="col-lg-4 col-md-6">
            <label style={labelStyle}>ชื่อบัญชี (ไทย)</label>
            <input
              type="text"
              className="form-control"
              name="Bank_Name_TH"
              value={formData.Bank_Name_TH || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div className="col-lg-4 col-md-6">
            <label style={labelStyle}>ชื่อบัญชี (อังกฤษ)</label>
            <input
              type="text"
              className="form-control"
              name="Bank_Name_EN"
              value={formData.Bank_Name_EN || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div className="col-lg-4 col-md-6">
            <label style={labelStyle}>ประเภทบัญชี</label>
            <select
              className="form-select"
              name="Bank_Type"
              value={formData.Bank_Type || "SA"}
              onChange={handleChange}
              style={selectStyle}
            >
              <option value="SA">ออมทรัพย์</option>
              <option value="CA">กระแสรายวัน</option>
            </select>
          </div>
          <div className="col-lg-4 col-md-6">
            <label style={labelStyle}>กลุ่มผู้ใช้</label>
            <select
              className="form-select"
              name="APAR_Type"
              value={formData.APAR_Type || "AP"}
              onChange={handleChange}
              style={selectStyle}
            >
              <option value="AP">เจ้าหนี้</option>
              <option value="AR">ลูกหนี้</option>
              <option value="CP">บัญชีของบริษัท</option>
            </select>
          </div>
          <div className="col-lg-4 col-md-6">
            <label style={labelStyle}>เจ้าของบัญชี</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                value={selectName}
                readOnly
                style={inputStyle}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowEntityModal(true)}
              >
                <i className="fas fa-search" />
              </button>
            </div>
          </div>
          <div className="col-lg-4 col-md-6">
            <label style={labelStyle}>
              ธนาคาร <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                value={selectBankName}
                readOnly
                style={inputStyle}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowBankModal(true)}
              >
                <i className="fas fa-search" />
              </button>
            </div>
          </div>
          <div className="col-lg-4 col-md-6">
            <label style={labelStyle}>สถานะ</label>
            <select
              className="form-select"
              name="Bank_Status"
              value={formData.Bank_Status || "Y"}
              onChange={handleChange}
              style={selectStyle}
            >
              <option value="Y">ใช้งาน</option>
              <option value="N">ไม่ใช้งาน</option>
            </select>
          </div>
        </div>
      </InitialDataFormLayout>

      {formData.APAR_Type === "AP" && (
        <ApModal
          showApModal={showEntityModal}
          handleApClose={() => setShowEntityModal(false)}
          apDataList={apDataList}
          onRowSelectAp={handleSelectEntity}
        />
      )}

      {formData.APAR_Type === "AR" && (
        <ArModal
          showArModal={showEntityModal}
          handleArClose={() => setShowEntityModal(false)}
          arDataList={arDataList}
          onRowSelectAr={handleSelectEntity}
        />
      )}

      {formData.APAR_Type === "CP" && (
        <CompModal
          showCompModal={showEntityModal}
          handleCompClose={() => setShowEntityModal(false)}
          compDataList={compDataList}
          onRowSelectComp={handleSelectEntity}
        />
      )}

      {showBankModal && (
        <BankModal
          showBankModal={showBankModal}
          handleBankClose={() => setShowBankModal(false)}
          bankDataList={bankDataList}
          onRowSelectBank={handleSelectBank}
        />
      )}
    </>
  );
};

export default BankForm;
